import _ from "lodash";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";

// components
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import AnimateHeight from "UIComponents/AnimateHeight";
import I18nHOC from "UIComponents/I18nHOC";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import ConfigureAIVoice from "AppComponents/ConfigureAIVoice";
import AILanguageSelection from "AppComponents/ConfigureAIVoice/components/AILanguageSelection";
import ContextSelection from "./components/ContextSelection";
import { CreateDropDown } from "Attachment";
import LinkifyModal from "./components/LinkifyModal";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import CircleLoading from "UIComponents/CircleLoading";

// design system
import { Button, Tooltip, IconButton, Modal } from "@toddle-edu/ds-web";
import {
  SendFilled,
  InformationOutlined,
  WarningTriangleFilled,
  MicrophoneOutlined,
} from "@toddle-edu/ds-icons";
import { fontStyles, elevations } from "@toddle-edu/ds-theme";

// third party
import classNames from "classnames";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import { Trans } from "react-i18next";

// utils
import {
  isOnlySpanTag,
  getFooterOptions,
  shouldRemoveBrTagFromEnd,
  isPromptEqual,
  getPlaceholder,
  moveCaretToEndOfContentEditable,
  getPromptLabelWithAttachments,
  getInsertAttachmentElement,
  getIsSendButtonDisabled,
} from "./modules/Utils";
import { useCallbackRef, isSafari, getUpdatedMaxSize } from "Utils";
import { isYoutubeLink } from "@toddle-edu/common-utils";
import ACLStore from "lib/aclStore";
import { wrapPlaceholderWithSpan } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/components/PromptCards/PromptCards";
import { getSettingValue } from "Utils/ConfigurationUtils";

// constants
import {
  MODAL_STATE,
  METADATA_PROMPT_FIELDS,
  ALLOWED_FILE_TYPES,
  CREATE_DROPDOWN_OPTIONS,
  DIALOGS_INFO,
  MODAL_ERRORS,
  INITIAL_DIALOG_STATE,
  MAX_SIZE_10_MEGA_BYTES,
  MAX_NUMBER_OF_FILES,
  ANIMATED_PLACEHOLDER_TEXTS,
} from "./modules/Constants";
import { ASSESSMENT } from "Constants/stringConstants";
import { GENERATE_QUESTIONS } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Constants";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";

// modules
import {
  getContextFieldsWithValue,
  createAIConversationAttachments,
  deleteAIConversationAttachment,
} from "./modules/AIContextBarModule";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getUserType,
} from "modules/CommonSelectors";
import { getAssessmentDetailsForAIEnhancer } from "Assessments/modules/AssessmentQueryEnhancers";
import {
  deleteAttachment,
  getSpeechToTextTranscript,
  updateAIConversation,
} from "modules/Services";
import { getUnitPlanCollaboratorsQuery } from "./modules/AIContextBarQueries";
import { updateShowAIQuickTip } from "Assessments/modules/AssessmentModule";

// scss
import classes from "./AIContextBar.scss";

const tooltipAlign = {
  attachment: {
    offset: [18, 0],
  },
  fields: {
    offset: [13, 0],
  },
};

let ANIMATED_PLACEHOLDER_TEXT_INDEX = 0;

const styles = {
  uiContentEditable: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    padding: "12px 16px",
    maxHeight: "150px",
    overflowY: "auto",
    ...fontStyles.medium,
  },
  uiContentEditableWithMinHeight: {
    minHeight: "64px",
    padding: "16px 12px",
  },
  overlayInnerStyle: {
    maxWidth: "280px",
  },
  placeholderWithContextList: {
    top: "16px",
    left: "12px",
  },
  aiTutorRecorderStyle: { background: "none" },
};

const CONTENT_EDITABLE_ID = "AI_INPUT_FIELD";
const ADDITIONAL_INSTRUCTIONS_ID = "additional-instructions";

/* Animation is not working smoothly in safari so not using it as of now */
const AnimateHeightWrapper = props => {
  const { showContextList, children } = props;

  if (!isSafari()) return <AnimateHeight {...props} />;
  else return showContextList ? children : null;
};

const TranscriptionListenerBase = () => null;

const TranscriptionListener = compose(
  withPusherBind([
    "fetchingTranscript",
    "uploadingEnhancedTranscript",
    "completed",
    "failed",
  ])
)(TranscriptionListenerBase);

const AIContextBar = props => {
  const {
    t,
    value: parentValue,
    onChange: parentOnChange,
    selectedAction,
    setSelectedAction,
    isInitialState,
    onSendMessage,
    isSendButtonDisabled,
    footerTopComp,
    context,
    setContext,
    assessmentDetails,
    unitPlanDetails,
    getContextFieldsWithValue,
    createAIConversationAttachments,
    curriculumType,
    assessmentTemplate,
    unitPlanTemplate,
    excludedFields,
    setExcludedFields,
    aiConversationId,
    course,
    creationMode,
    sourceFrom,
    mode,
    isFromUnitPlan,
    middlePaneRef,
    isDraft,
    portalType,
    templateId,
    isWritingMessage,
    isSuggestionLibraryDisabled,
    gradeId,
    entityId,
    entityType: promptSetEntityType,
    setConfigureAIVoiceRef,
    onClickFooterOption,
    parentType,
    conversationMetadata,
    updateAIConversation,
    updateShowAIQuickTip,
    showAIQuickTip,
    isQuestionAssistant,
    isAIAssessmentTools,
    getSpeechToTextTranscript,
    question,
    chatInputPlaceholder,
    isSpeechEnabled,
    conversationAttachments,
    isAttachmentUploading,
    prompts,
    deleteAIConversationAttachment,
    isAITutor,
    initialValue,
    isUnitPlanAI,
    onClickContextField,
    learningCourseId,
    isAttachmentUploadDisabled,
    showFooterOverlay,
  } = props;

  // states

  const [value, setValue] = useState(parentValue || "");
  const [showContextList, setShowContextList] = useState(false);
  const [selectedField, setSelectedField] = useState({
    modalState: MODAL_STATE.NULL,
  });
  const [isContentEditableFocused, setIsContentEditableFocused] = useState(
    false
  );
  const [showQuickTip, setShowQuickTip] = useState(false);
  const [isCreateDropdownVisible, setIsCreateDropdownVisible] = useState(false);
  const [currentDialogInfo, setCurrentDialogInfo] = useState(
    INITIAL_DIALOG_STATE
  );
  const [showLinkifyModal, setShowLinkifyModal] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [isAIGeneratingResponse, setIsAIGeneratingResponse] = useState(false);
  const [isContentEditableDisabled, setIsContentEditableDisabled] = useState(
    false
  );
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioTranscriptLoading, setIsAudioTranscriptLoading] = useState(
    false
  );
  const [isCreatingAttachments, setIsCreatingAttachments] = useState(false);
  const [isTranscriptionPending, setIsTranscriptionPending] = useState(false);
  const [pendingTranscriptionUrls, setPendingTranscriptionUrls] = useState([]);
  const [pendingSendIntent, setPendingSendIntent] = useState(false);

  // refs

  const footerRef = useRef(null);
  const valueRef = useRef(value);
  const audioContainerRef = useRef(null);
  const contextSelectionContainerRef = useRef(null);
  const [contentEditableRef, setContentEditableRef] = useCallbackRef();
  const recordButtonRef = useRef(null);

  // side effects

  // specific for worksheet
  useEffect(() => {
    if (isQuestionAssistant) {
      parentOnChange(value);
    }
  }, [value, isQuestionAssistant]);

  // set starting text on mount
  useEffect(() => {
    if (!_.isEmpty(initialValue)) {
      setValue(initialValue);
    }
  }, []);

  // when a prompt is selected this will set that prompt to the content editable div and focus on it
  useEffect(() => {
    if (!_.isEmpty(selectedAction)) {
      const { label, shouldFocusContentEditable = true } = selectedAction || {};

      // in case of blank question, default action should be selected and text should filled in input box
      const isBlankQuestionFlow =
        _.isEmpty(_.get(question, "questionTitle")) &&
        _.isEqual(_.get(selectedAction, "action"), GENERATE_QUESTIONS);

      if (
        isQuestionAssistant &&
        isEveryRequiredFieldFilled &&
        !isBlankQuestionFlow
      ) {
        onSendMessageLocal();
      } else if (contentEditableRef?.current && label) {
        if (isAITutor) {
          const updatedLabel = getPromptLabelWithAttachments({
            t,
            promptLabel: label,
            attachments: conversationAttachments,
          });

          setValue(
            `<span contenteditable="false" style="user-select: auto;">${updatedLabel} <span id="${ADDITIONAL_INSTRUCTIONS_ID}" placeholder="${t(
              "ai:ai_tutor_prompt_selected_placeholder"
            )}" class="${
              classes.additionalInstructionSpan
            }" role="textbox" tabindex="0" contenteditable="true"></span>
              </span> `
          );

          setTimeout(() => {
            moveCaretToEndOfContentEditable(contentEditableRef.current);
            setIsContentEditableFocused(true);
          }, 0);
        } else {
          setValue(
            `<span contenteditable=false style="user-select: none;">${label}; </span> `
          );
        }

        setTimeout(() => {
          setShowContextList(!isEveryRequiredFieldFilled);
          if (shouldFocusContentEditable) contentEditableRef?.current?.focus();
        });
      }
    }
  }, [selectedAction]);

  // when the value of the content editable div changes, this will update the placeholder
  useEffect(() => {
    const _contentEditableRef = contentEditableRef?.current;

    if (_contentEditableRef) {
      const placeholder = !_.isEmpty(chatInputPlaceholder)
        ? chatInputPlaceholder
        : getPlaceholder({
            t,
            value,
            selectedAction,
            isAITutor,
            isInitialState,
          });

      // specifically for firefox (or browsers with same issue) - https://stackoverflow.com/questions/5705580/contenteditable-firefox-br-tag
      if (
        shouldRemoveBrTagFromEnd({ selectedAction, ref: _contentEditableRef })
      ) {
        _contentEditableRef.removeChild(_contentEditableRef.lastChild);
      }

      // for AITutor, placeholder is shown with animated text
      if (isAITutor && _.isEmpty(value) && isInitialState) {
        _contentEditableRef.removeAttribute("placeholder");
        return;
      }

      // update placeholder through setAttribute as contentEditable div does not support placeholder attribute
      if (_contentEditableRef.getAttribute("placeholder") !== placeholder) {
        _contentEditableRef.setAttribute("placeholder", placeholder);
      }
    }
  }, [selectedAction, value, isAITutor, isInitialState]);

  // close the context list when clicked outside of the footer expect when the modal is open
  useEffect(() => {
    // we do not want it to close when user is on left pane
    if (isUnitPlanAI) return;

    const { modalState } = selectedField || {};
    const { OPEN, CLOSED } = MODAL_STATE;

    const handleClickOutside = e => {
      const target = e.target;

      // Clicking on modal is detected as outside click so if modal is open then return
      if (modalState === OPEN) return;
      else if (modalState === CLOSED) {
        // if modal is closed then allow to close the context
        setSelectedField({ modalState: MODAL_STATE.NULL });
        return;
      }

      const isOutsideClickFromFooter =
        footerRef.current && !footerRef.current.contains(target);

      if (isOutsideClickFromFooter) {
        setShowContextList(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => document.removeEventListener("click", handleClickOutside);
  }, [selectedField]);

  // show quick tip when the content editable div is focused for the first time
  useEffect(() => {
    let timeout = null;

    if (isContentEditableFocused && showAIQuickTip && isInitialState) {
      // show toast after .3s
      setTimeout(() => setShowQuickTip(true), 300);
      // hide toast after 3s
      timeout = setTimeout(hideQuickTip, 3000);
    }
    return () => (timeout ? clearTimeout(timeout) : null);
  }, [showAIQuickTip, isInitialState, isContentEditableFocused]);

  // show context list on mount if required fields are empty
  useEffect(() => {
    if (isQuestionAssistant) {
      openContextWindow();
    }
  }, []);

  // whenever user uploads attachments, we need to open the context bar and scroll to the end
  // also close the dropdown once the attachment is starts uploading
  useEffect(() => {
    if (isAttachmentUploading) {
      openContextWindowAndScrollToEnd();
      setIsCreateDropdownVisible(false);
    }
  }, [isAttachmentUploading]);

  // disable content editable when an action is selected, this make sure that user is only able to edit
  // the placeholder text (##type here##) in the selectedAction
  useEffect(() => {
    if (isAITutor) {
      if (!_.isEmpty(selectedAction) && !_.isEmpty(value)) {
        setIsContentEditableDisabled(true);
      } else {
        setIsContentEditableDisabled(false);
      }
    }
  }, [isAITutor, selectedAction, value]);

  // update valueRef when value changes
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // variables

  const contextFields = getContextFieldsWithValue({
    t,
    context,
    template: assessmentTemplate,
    selectedAction,
    assessmentTemplate,
    unitPlanTemplate,
    assessmentDetails,
    unitPlanDetails,
    curriculumProgramType: curriculumType,
    excludedFields,
    course,
    creationMode,
    sourceFrom,
    assessmentMode: mode,
    isFromUnitPlan,
    middlePaneRef,
    isDraft,
    conversationMetadata,
    conversationAttachments,
    isQuestionAssistant,
    isBlankQuestion: _.get(question, "isBlankQuestion"),
    prompts,
    isAITutor,
    templateId,
    isUnitPlanAI,
  });

  const shouldRenderContentEditable = useMemo(() => {
    if (isAudioTranscriptLoading) {
      return !_.isEmpty(value);
    }

    return true;
  }, [value, isAudioTranscriptLoading]);

  const _isSendButtonDisabled =
    isSendButtonDisabled ||
    isWritingMessage ||
    isCreatingAttachments ||
    (!isQuestionAssistant &&
      getIsSendButtonDisabled({
        value,
        isAITutor,
        selectedAction,
        isAttachmentUploading,
      }));

  const prerequisitesWithEmptyValue = _.filter(contextFields, contextField => {
    const { value, isRequired = false } = contextField || {};
    return isRequired && _.isEmpty(value);
  });

  const isEveryRequiredFieldFilled = _.isEmpty(prerequisitesWithEmptyValue);

  const footerOptions = getFooterOptions({
    showContextList,
    contextFields,
    isQuestionAssistant,
    conversationAttachments,
    isSuggestionLibraryDisabled,
    isAITutor,
    isAttachmentUploadDisabled,
  });

  // functions

  const onClickContentEditable = async event => {
    const isCloseIconClick = event.target.id === "data-suffix-icon";

    const attachmentElement = event.target.closest("#attachment-text");

    const attachmentId = _.get(attachmentElement, "dataset.attachmentId");

    // If close icon is clicked then delete the attachment
    if (isCloseIconClick) {
      try {
        await deleteAIConversationAttachment({
          input: { attachmentIds: [attachmentId] },
          aiConversationId,
        });

        onAttachmentsUpdate({ id: attachmentId, type: "DELETE" });

        const updatedLabel = contentEditableRef.current.innerHTML;
        setValue(updatedLabel);

        // if there are no attachments after deleting the current one, reset the selected action
      } catch (e) {}
      return;
    }

    // If attachment element is clicked then show the linkify modal
    if (attachmentElement?.contains(event.target)) {
      setShowLinkifyModal(true);
      const selectedAttachmentId =
        attachmentId === "undefined" ? null : attachmentId;
      setSelectedAttachmentId(selectedAttachmentId);
    }
  };

  const hideQuickTip = () => {
    updateShowAIQuickTip(false);
    setShowQuickTip(false);
  };

  const formatValueForUnsupportedBrowsers = value => {
    let formattedValue = _.trim(value);

    if (formattedValue === "<br>") formattedValue = "";

    if (_.endsWith(formattedValue, "</span>"))
      formattedValue = formattedValue + " ";

    return formattedValue;
  };

  const updateInputField = value => {
    if (showQuickTip) hideQuickTip();

    const formattedValue = formatValueForUnsupportedBrowsers(value);

    if (_.isEmpty(formattedValue) && !_.isEmpty(selectedAction)) {
      setSelectedAction({});
    }

    setValue(formattedValue);
  };

  const onSendMessageLocal = async () => {
    if (isTranscriptionPending) {
      setPendingSendIntent(true);
      return;
    }

    const metaDataPromptFields = _.get(
      conversationMetadata,
      "promptFields",
      []
    );

    const promptFields = _.map(contextFields, field =>
      _.pick(field, METADATA_PROMPT_FIELDS)
    );

    if (!isQuestionAssistant && !isAITutor) {
      // if there is no promptFields metadata then update the metadata
      if (aiConversationId && _.isEmpty(metaDataPromptFields)) {
        updateAIConversation({
          input: {
            id: aiConversationId,
            metadata: {
              ...conversationMetadata,
              promptFields,
            },
          },
        });
      } else if (
        aiConversationId &&
        !_.isEqualWith(promptFields, metaDataPromptFields, isPromptEqual) // if promptFields are not equal to the metadata promptFields then update the metadata
      ) {
        updateAIConversation({
          input: {
            id: aiConversationId,
            metadata: { ...conversationMetadata, promptFields },
          },
        });
      }
    }

    const content = _.cloneDeep(value);

    setValue("");
    setShowContextList(false);
    setIsAIGeneratingResponse(true);
    await onSendMessage({ content, promptFields });
    setIsAIGeneratingResponse(false);
  };

  const onKeyDown = e => {
    if (showQuickTip) hideQuickTip();

    if (
      e?.key === "Backspace" &&
      e?.target?.textContent === "" &&
      e?.target?.id === ADDITIONAL_INSTRUCTIONS_ID
    ) {
      setValue("");
      if (!_.isEmpty(selectedAction)) setSelectedAction({});
      setTimeout(() => contentEditableRef.current?.focus(), 0);
      return;
    }

    const isEnterPressed = e.keyCode === 13;
    const isShiftPressed = e.shiftKey;

    if (isEnterPressed && !isShiftPressed) {
      e.preventDefault();
      if (
        isEveryRequiredFieldFilled &&
        !_isSendButtonDisabled &&
        !isAIGeneratingResponse
      ) {
        onSendMessageLocal();
      } else {
        openContextWindow();
      }
    }
  };

  const onBlurInputField = () => {
    hideQuickTip();
    setIsContentEditableFocused(false);
  };

  // if any prerequisite if not filled then on click of send button open the context window
  const openContextWindow = () => {
    if (!isEveryRequiredFieldFilled) setShowContextList(true);
  };

  const openContextWindowAndScrollToEnd = () => {
    // if context window is not enabled then return
    if (_.isEmpty(contextFields)) return;

    setShowContextList(true);

    if (contextSelectionContainerRef?.current) {
      setTimeout(() => {
        contextSelectionContainerRef.current.scrollTop =
          contextSelectionContainerRef.current.scrollHeight;
      });
    }
  };

  const onAttachmentsUpdate = ({ id, type, label, prevAttachmentId }) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = valueRef.current;

    // handle attachment delete
    if (type === "DELETE") {
      const attachmentElement = tempElement.querySelector(
        `[data-attachment-id="${id}"]`
      );

      // When deleting the last attachment, replace the placeholder with 'insert attachment' element
      // else just remove the attachment
      if (_.size(conversationAttachments) === 1) {
        const insertAttachmentEle = getInsertAttachmentElement(selectedAction);
        if (insertAttachmentEle) {
          attachmentElement?.replaceWith(insertAttachmentEle);
        } else {
          attachmentElement?.remove();
        }
      } else {
        attachmentElement?.remove();
      }
    } else {
      // editing existing attachment
      if (selectedAttachmentId) {
        const elemToUpdate = tempElement.querySelector(
          `[data-attachment-id="${prevAttachmentId}"]`
        );
        // Locate nodes with id of 'attachment-content' and update its innerText
        _.forEach(elemToUpdate.children, node => {
          if (node.id === "attachment-content") {
            node.innerText = ` ${label} `;
          }
        });

        // update the selected attachment id of the node
        elemToUpdate.dataset.attachmentId = id;
      } else {
        // When adding first attachment, the 'insert attachment' btn is replaced
        if (_.isEmpty(conversationAttachments)) {
          const insertAttachmentEle = tempElement.querySelector(
            "#attachment-text"
          );

          // Replace insertAttachmentEle with textNode
          if (insertAttachmentEle) {
            const textNode = document.createTextNode(`##${label}##`);
            insertAttachmentEle.replaceWith(textNode);
          }
        } else {
          // append the new attachment label at appropriate position
          const textNode = document.createTextNode(`##${label}##`);
          const lastAttachmentElement = tempElement.querySelector(
            "#attachment-text:last-child"
          );
          lastAttachmentElement?.insertAdjacentText(
            "afterend",
            textNode.textContent
          );
        }
      }
    }

    const updatedLabelWithSpan = wrapPlaceholderWithSpan({
      t,
      label: tempElement.innerHTML,
      attachmentId: id,
      showEditIcon: true,
    });

    setValue(updatedLabelWithSpan);
  };

  const createConversationAttachments = async attachments => {
    return await createAIConversationAttachments({
      input: _.map(attachments, attachment => ({
        ..._.omit(attachment, ["id", ...ATTACHMENT_OMIT_ATTRIBUTES]),
        parentId: aiConversationId,
        parentType: "AI_CONVERSATION",
      })),
      aiConversationId,
      updateTitleAfterCreation: true,
    });
  };

  const onContentUploaded = async attachments => {
    if (!aiConversationId || _.isEmpty(attachments)) return;

    setIsCreatingAttachments(true);

    let response;
    // for adding new attachments
    if (!selectedAttachmentId) {
      response = await createConversationAttachments(attachments);
    } else {
      // editing existing attachment
      const newURL = _.first(attachments)?.url;
      const oldURL = attachment?.url;

      // if the url is not changed then close the linkify modal
      // without making any updates
      if (newURL === oldURL) {
        onCloseLinkifyModal();
        setSelectedAttachmentId(null);
        setIsCreatingAttachments(false);
        return;
      }

      // delete the existing attachment
      await deleteAIConversationAttachment({
        input: { attachmentIds: [selectedAttachmentId] },
        aiConversationId,
      });

      // create the new attachment
      response = await createConversationAttachments(attachments);
    }

    // specifically handled for links where content does not get scraped
    try {
      const isLink = _.get(response, "0.type") === "LINK";
      const url = _.get(response, "0.url");
      const linkHostName = new URL(url).hostname;
      const tokenCount = _.get(
        response,
        "0.metadata.extractedContentDetails.tokenCount",
        0
      );

      const attachmentIdFromResponse = _.get(response, "0.id");

      // Track YouTube links for transcription processing
      if (isLink && url && isYoutubeLink(url)) {
        setIsTranscriptionPending(true);
        setPendingTranscriptionUrls(prev => [...prev, url]);
      } else if (isLink && linkHostName && tokenCount === 0) {
        setCurrentDialogInfo({
          action: MODAL_ERRORS.ZERO_TOKENS,
          params: {
            label: linkHostName,
            attachmentId: attachmentIdFromResponse,
          },
        });
        return;
      }

      // for LINK there is no uploading state so handled here, for other attachments it is being handled in useEffect
      // when link is added, attachments array will always contain only 1 item
      if (_.get(attachments, "0.type") === "LINK" && !showLinkifyModal) {
        openContextWindowAndScrollToEnd();
      }

      const attachmentResponse = _.first(response);
      const { name, id } = attachmentResponse;

      if (!_.isEmpty(selectedAction)) {
        onAttachmentsUpdate({
          id,
          label: name,
          prevAttachmentId: selectedAttachmentId,
        });
      }

      if (showLinkifyModal) {
        onCloseLinkifyModal();
        setSelectedAttachmentId(null);
      }
    } catch (err) {}

    setIsCreatingAttachments(false);
  };

  const onTranscriptionEvent = useCallback(
    params => {
      const { event, videoUrl } = params || {};
      if (!pendingTranscriptionUrls.includes(videoUrl)) return;

      if (event === "completed" || event === "failed") {
        setPendingTranscriptionUrls(prev => {
          const next = prev.filter(u => u !== videoUrl);
          if (next.length === 0) setIsTranscriptionPending(false);
          return next;
        });
      }
    },
    [pendingTranscriptionUrls]
  );

  // Auto-send when transcription completes and user had clicked send
  useEffect(() => {
    if (pendingSendIntent && !isTranscriptionPending) {
      setPendingSendIntent(false);
      onSendMessageLocal();
    }
  }, [pendingSendIntent, isTranscriptionPending]);

  const customFilterFilesHandler = files => {
    const numberOfFiles = _.size(files);
    const numberOfAttachments = _.size(conversationAttachments);

    // CASE 1 - show error model when number of files are more than 5
    if (numberOfFiles + numberOfAttachments > 5) {
      setCurrentDialogInfo({
        action: MODAL_ERRORS.FILE_COUNT,
        params: { label: MAX_NUMBER_OF_FILES },
      });

      setIsCreateDropdownVisible(false);

      return [];
    }

    // CASE 2 - show error model when file size is more than 10MB
    const filesWithinLimit = _.filter(files, file => {
      const { type, size, sizeBytes } = file || {};

      const updatedMaxSize = getUpdatedMaxSize({
        maxSize: MAX_SIZE_10_MEGA_BYTES,
        fileType: type,
      });

      return updatedMaxSize >= (size || sizeBytes || 0);
    });

    if (_.size(filesWithinLimit) !== _.size(files)) {
      const filesExceedingLimit = _.differenceBy(files, filesWithinLimit, [
        "preview",
        "url",
      ]);

      const names = _.map(filesExceedingLimit, "name");

      setCurrentDialogInfo({
        action: MODAL_ERRORS.FILE_SIZE,
        params: { label: names },
      });

      setIsCreateDropdownVisible(false);

      return filesWithinLimit;
    }

    // return files as it is if no error
    return files;
  };

  const onClickDialogButton = async () => {
    const { action, params } = currentDialogInfo || {};
    const { attachmentId } = params || {};

    switch (action) {
      case MODAL_ERRORS.FILE_COUNT:
      case MODAL_ERRORS.FILE_SIZE: {
        setCurrentDialogInfo(INITIAL_DIALOG_STATE);
        setIsCreateDropdownVisible(false);
        break;
      }

      case MODAL_ERRORS.ZERO_TOKENS: {
        if (attachmentId) {
          await deleteAIConversationAttachment({
            input: { attachmentIds: [attachmentId] },
            aiConversationId,
          });
        }

        setCurrentDialogInfo(INITIAL_DIALOG_STATE);
        setIsCreateDropdownVisible(false);
        break;
      }
    }
  };

  const onCloseLinkifyModal = () => {
    setShowLinkifyModal(false);
  };

  const getLinkedAttachment = params => {
    const { url, linkType, title, mimeType, type, fileType } = params;

    return {
      url,
      name: title,
      mimeType,
      type: linkType === "URL" ? type : fileType,
    };
  };

  const onInsertAttachment = async params => {
    const linkedAttachment = getLinkedAttachment(params);
    await onContentUploaded([linkedAttachment]);
  };

  const handleTranscription = async ({ attachment }) => {
    const transcriptionPayload = {
      entityId,
      entityType: promptSetEntityType,
    };

    if (ACLStore.can("FeatureFlag:EnableSTTLanguageInput")) {
      if (conversationMetadata?.languageCode) {
        transcriptionPayload.language = conversationMetadata.languageCode;
      }
    }

    transcriptionPayload.blobAttachment = attachment;

    const transcript = (await getSpeechToTextTranscript(transcriptionPayload))
      ?.transcript;

    if (transcript) {
      if (!_.isEmpty(selectedAction)) {
        updateInputField(`${transcript}`);
      } else {
        updateInputField(`${value} ${transcript}`);
      }
    }

    setIsAudioTranscriptLoading(false);
    setIsContentEditableDisabled(false);
    setSelectedAction({});

    if (contentEditableRef.current) {
      contentEditableRef.current.focus();
      moveCaretToEndOfContentEditable(contentEditableRef.current);
      setIsContentEditableFocused(true);
    }
  };

  const onBlobAttachmentRecieved = async blobAttachment => {
    setIsAudioTranscriptLoading(true);
    setIsContentEditableDisabled(true);

    if (!_.isEmpty(selectedAction)) {
      setValue("");
    }

    await handleTranscription({ attachment: blobAttachment });
  };

  const { action: currentDialogAction, params: currentDialogParams = {} } =
    currentDialogInfo || {};

  const attachment = _.find(conversationAttachments, {
    id: selectedAttachmentId,
  });

  const showFocusedContainer = useMemo(
    () => !_.isEmpty(initialValue) && _.isEqual(value, initialValue),
    [value, initialValue]
  );

  const showBoxShadow =
    showContextList || isContentEditableFocused || showFocusedContainer;

  const isContextEnabled = !_.isEmpty(contextFields);

  const enableAIPromptSetSelection = getSettingValue({
    name: "Common:EnableAIPromptSetSelection",
    readFromUserAndOrgLevel: true,
  });

  // classes
  const contentEditableClass = classNames({
    [classes.contentEditable]: true,
    [classes.aiTutorContentEditable]: isAITutor,
    [classes.contentEditableWithPrompt]:
      !_.isEmpty(selectedAction) && isOnlySpanTag(value) && !isAITutor,
  });

  const footerOptionsClass = classNames({
    [classes.footerOptions]: true,
    [classes.footerOptionsForContext]: isContextEnabled,
  });

  const footerInnerContainerClass = classNames({
    [classes.footerInnerContainer]: true,
    [classes.footerInnerContainerAITutor]: isAITutor,
    [classes.footerInnerContainerHover]: !showBoxShadow,
    [classes.focusedFooterInnerContainer]: showBoxShadow,
    [classes.footerInnerContainerForQuestionAssistant]: isQuestionAssistant,
    [classes.footerInnerContainerUnitAI]: isUnitPlanAI,
  });

  const footerOverlayClass = classNames({
    [classes.footerOverlay]: true,
    [classes.footerOverlayWithContextExpanded]: showContextList,
    [classes.footerOverlayForQuestionAssistant]: isQuestionAssistant,
  });

  const uiContentEditableStyles = {
    ...styles.uiContentEditable,
    ...(showContextList ? styles.uiContentEditableWithMinHeight : {}),
  };

  const footerClasses = classNames({
    [classes.footerWrapper]: true,
    [classes.footerWrapperForQuestionAssistant]: isQuestionAssistant,
  });

  const contentEditablePlaceholder = isAITutor
    ? "" // animated placeholder is shown for AI tutor
    : !_.isEmpty(chatInputPlaceholder)
    ? chatInputPlaceholder
    : t("classRoom:start_typing_to_get_suggestions");

  const handlePlaceholderAnimationEnd = e => {
    const animatedTextContainer = e.target;

    // update the text index to be shown
    ANIMATED_PLACEHOLDER_TEXT_INDEX =
      (ANIMATED_PLACEHOLDER_TEXT_INDEX + 1) %
      _.size(ANIMATED_PLACEHOLDER_TEXTS);

    // update placeholder text
    animatedTextContainer.innerText = t(
      ANIMATED_PLACEHOLDER_TEXTS[ANIMATED_PLACEHOLDER_TEXT_INDEX]
    );

    // Reset animation by forcing a reflow
    animatedTextContainer.offsetHeight; // trigger reflow
    animatedTextContainer.style.animation = "none"; // Disable the animation
    animatedTextContainer.offsetHeight; // trigger reflow again
    animatedTextContainer.style.animation = ""; // Re-enable the animation;
  };

  const onFileUpload = () => {
    setIsAudioTranscriptLoading(true);
    setIsContentEditableDisabled(true);
  };

  const renderAudioRecorder = () => {
    if (!isSpeechEnabled) return null;

    const buttonComponent = (
      <IconButton
        icon={<MicrophoneOutlined />}
        type={"plain"}
        variant={"neutral"}
        size={"small"}
        onClick={() => setIsRecording(true)}
        disabled={isAudioTranscriptLoading}
        testId={`assessment-aiContextBar-audioButton`}
        dsVersion={"2.0"}
        ref={recordButtonRef}
      />
    );

    return (
      <div>
        <AudioRecorderSelector
          parentType={"AI_CONVERSATION"}
          parentId={aiConversationId}
          setChatbarVisible={value => setIsRecording(!value)}
          header={t("common:add_voice_message")}
          version={"inline"}
          buttonComponent={buttonComponent}
          buttonComponentTooltip={t("common:dictate")}
          onFileUpload={onFileUpload}
          disabled={isAttachmentUploading || isAudioTranscriptLoading}
          showValue={false}
          showProgress={false}
          recordButtonRef={recordButtonRef}
          onBlobAttachmentRecieved={onBlobAttachmentRecieved}
          loaderStyles={isAITutor ? styles.aiTutorRecorderStyle : {}}
        />
      </div>
    );
  };

  return (
    <footer className={footerClasses}>
      <div
        className={footerInnerContainerClass}
        style={showBoxShadow ? elevations["elevation-2B"] : {}}
        ref={footerRef}
      >
        {/* for assessments we use footerTopComp to render "Stop generating" button */}
        {footerTopComp && (isQuestionAssistant ? !showContextList : true)
          ? footerTopComp
          : null}

        {showQuickTip ? (
          <div className={classes.quickTip} style={elevations["elevation-2B"]}>
            <InformationOutlined variant={"on"} size={"xxx-small"} />
            <span className={classes.quickTipText}>
              {t("ai:assessment_quick_tip_msg")}
            </span>
          </div>
        ) : null}

        {/* Overlay for Audio Recorder */}
        {isSpeechEnabled && isRecording ? (
          <div
            className={classes.recordingOverlay}
            ref={audioContainerRef}
          ></div>
        ) : null}

        {/* Input box */}
        <div className={contentEditableClass}>
          {shouldRenderContentEditable ? (
            <UIBaseContentEditable
              id={CONTENT_EDITABLE_ID}
              value={value}
              placeholder={contentEditablePlaceholder}
              updateInputField={updateInputField}
              style={uiContentEditableStyles}
              minRows={1}
              maxRows={5}
              disabled={isContentEditableDisabled}
              onKeyDown={onKeyDown}
              updateInputRef={setContentEditableRef}
              shouldParseInPlainText={false}
              onFocusInputField={() => setIsContentEditableFocused(true)}
              onBlurInputField={onBlurInputField}
              onClickContentEditable={onClickContentEditable}
            />
          ) : null}
          {isSpeechEnabled && isAudioTranscriptLoading ? (
            <div
              className={classNames(classes.voiceLoaderContainer, {
                [classes.voiceLoaderWithContentEditable]: shouldRenderContentEditable,
              })}
            >
              <div className={classes.voiceLoader}>
                <div className={classes.voiceBar}></div>
                <div className={classes.voiceBar}></div>
                <div className={classes.voiceBar}></div>
                <div className={classes.voiceBar}></div>
              </div>
              <div className={classNames("text-body", classes.animatingText)}>
                {t("ai:transcribing")}
              </div>
            </div>
          ) : null}
          {/* placeholder with animated text */}
          {_.isEmpty(value) &&
          isAITutor &&
          isInitialState &&
          !isAudioTranscriptLoading ? (
            <span
              className={classes.placeholder}
              style={showContextList ? styles.placeholderWithContextList : {}}
            >
              <span>
                {t("ai:start_by_defining_your_tutoring_objective_placeholder")}
                &nbsp;
              </span>
              <span
                className={classes.animatedPlaceholderText}
                onAnimationEnd={handlePlaceholderAnimationEnd}
              >
                {t("ai:exit_ticket_for_a_lesson_on_periodic_table_elements")}
              </span>
            </span>
          ) : null}
        </div>

        {/* Context list with animation */}
        <AnimateHeightWrapper
          height={showContextList && !_.isEmpty(contextFields) ? "auto" : 0}
          duration={300}
          showContextList={showContextList && !_.isEmpty(contextFields)}
        >
          <ContextSelection
            contextFields={contextFields}
            selectedAction={selectedAction}
            portalType={portalType}
            assessmentTemplate={assessmentTemplate}
            unitPlanTemplate={unitPlanTemplate}
            assessmentDetails={assessmentDetails}
            unitPlanDetails={unitPlanDetails}
            context={context}
            setContext={setContext}
            mode={mode}
            middlePaneRef={middlePaneRef}
            isFromUnitPlan={isFromUnitPlan}
            sourceFrom={sourceFrom}
            setSelectedAction={setSelectedAction}
            course={course}
            excludedFields={excludedFields}
            setExcludedFields={setExcludedFields}
            templateId={templateId}
            selectedField={selectedField}
            setSelectedField={setSelectedField}
            isQuestionAssistant={isQuestionAssistant}
            isAIAssessmentTools={isAIAssessmentTools}
            question={question}
            showContextList={showContextList}
            conversationMetadata={conversationMetadata}
            conversationAttachments={conversationAttachments}
            aiConversationId={aiConversationId}
            isWritingMessage={isWritingMessage}
            containerRef={contextSelectionContainerRef}
            onAttachmentsUpdate={onAttachmentsUpdate}
            testId={`assessment-aiContextBar-contextSelection`}
            isUnitPlanAI={isUnitPlanAI}
            onClickContextField={onClickContextField}
            learningCourseId={learningCourseId}
          />
        </AnimateHeightWrapper>

        {/* Footer actions like "Add context", "Language" etc. */}
        <div className={classes.actionsBar}>
          <div className={footerOptionsClass}>
            {_.map(footerOptions, (option, index) => {
              const {
                id,
                label,
                icon,
                isVisible = true,
                tooltip,
                disabledTooltip,
                showTooltip = false,
                disabled = false,
              } = option;

              if (!isVisible) return null;

              let comp = null;

              const isLastOption = _.size(footerOptions) === index + 1;
              const tooltipLabel =
                disabled && disabledTooltip
                  ? disabledTooltip
                  : tooltip
                  ? tooltip
                  : label;

              const _onClickFooterOption = () => {
                switch (id) {
                  case "add_context":
                    if (!showContextList) contentEditableRef.current.focus();
                    setShowContextList(prevState => !prevState);
                    break;
                  default:
                    onClickFooterOption?.(id);
                }
              };

              switch (id) {
                case "language": {
                  comp = (
                    <AILanguageSelection
                      conversationId={aiConversationId}
                      gradeId={gradeId}
                      entityType={parentType}
                      customRef={setConfigureAIVoiceRef}
                      isDisabled={isWritingMessage || disabled}
                      conversationMetadata={conversationMetadata}
                      mode={mode}
                      testId={"assessment-aiContextBar"}
                    />
                  );
                  break;
                }
                case "add_attachment": {
                  const buttonComponent = (
                    <span>
                      <Tooltip
                        tooltip={
                          <span className={classes.tooltipContainer}>
                            {t(tooltipLabel)}
                          </span>
                        }
                        visible={showTooltip}
                        showArrow={true}
                      >
                        <Button
                          id={id}
                          icon={icon}
                          type={"plain"}
                          variant={"neutral"}
                          size={"small"}
                          dsVersion={"2.0"}
                          disabled={isWritingMessage || disabled}
                          testId={`assessment-aiContextBar-addAttachment`}
                        >
                          {t(label)}
                        </Button>
                      </Tooltip>
                    </span>
                  );

                  comp = (
                    <CreateDropDown
                      exportAsPdf={true}
                      parentId={aiConversationId}
                      parentType={"AI_CONVERSATION"}
                      accept={ALLOWED_FILE_TYPES}
                      mode={"edit"}
                      multiselect={true}
                      buttonComponent={buttonComponent}
                      dropDownOptions={CREATE_DROPDOWN_OPTIONS}
                      isNewDropdown={true}
                      onMultipleFilesUploaded={onContentUploaded}
                      disabled={isWritingMessage || disabled}
                      dsVersion={"2.0"}
                      enableShareModal={false} // do not show DriveShareModal, directly add as a file
                      visible={isCreateDropdownVisible}
                      onVisibleChange={setIsCreateDropdownVisible}
                      customFilterFilesHandler={customFilterFilesHandler}
                      showDisplayText={false} // do not show display text field for link (not required for AI)
                    />
                  );
                  break;
                }
                default: {
                  comp = (
                    <Tooltip
                      tooltip={
                        <span className={classes.tooltipContainer}>
                          {t(tooltipLabel)}
                        </span>
                      }
                      visible={showTooltip}
                      showArrow={true}
                    >
                      <Button
                        id={id}
                        icon={icon}
                        type={"plain"}
                        variant={"neutral"}
                        size={"small"}
                        onClick={_onClickFooterOption}
                        dsVersion={"2.0"}
                        disabled={disabled}
                        testId={`assessment-aiContextBar-${_.replace(
                          id,
                          "_",
                          "-"
                        )}`}
                      >
                        <span className={classes.buttonLabel}>{t(label)}</span>
                      </Button>
                    </Tooltip>
                  );
                }
              }

              return (
                <React.Fragment key={id}>
                  {isLastOption ? <span className={classes.separator} /> : null}
                  {comp}
                </React.Fragment>
              );
            })}
          </div>

          <div className={classes.flexWithGap}>
            {/* To change prompt set (for internal users only) */}
            {enableAIPromptSetSelection && !isQuestionAssistant ? (
              <ConfigureAIVoice
                conversationId={aiConversationId}
                gradeId={gradeId}
                entityType={parentType}
                headerSubtitle={t("classRoom:ai_voice_modal_subtext")}
                size={"small"}
                promptSetEntityType={promptSetEntityType}
                customRef={null}
                isDisabled={isWritingMessage}
                showLanguageDropdown={false}
              />
            ) : null}

            {isSpeechEnabled ? renderAudioRecorder() : null}

            {/* Send button */}
            <div className={classes.flex} onClick={openContextWindow}>
              {isTranscriptionPending && pendingSendIntent ? (
                <Tooltip
                  tooltip={t("ai:processing_video_subtext")}
                  placement={"topRight"}
                  align={tooltipAlign.attachment}
                  overlayInnerStyle={styles.overlayInnerStyle}
                  showArrow={true}
                >
                  <CircleLoading
                    size="x-small"
                    backgroundColor={"var(--surface-tertiary-enabled)"}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  tooltip={
                    isAttachmentUploading || isCreatingAttachments ? (
                      t("classRoom:almost_ready_loading_in_progress")
                    ) : !isEveryRequiredFieldFilled ? (
                      <Trans i18nKey="ai:please_fill_the_details_from_context_section">
                        <span>
                          Please fill in the following details from the
                          {" ‘Context’ "}
                          section:
                        </span>
                        <ul className={classes.list}>
                          {_.map(prerequisitesWithEmptyValue, prerequisite => {
                            const { field, type, label } = prerequisite || {};
                            const key = `${field}_${type}`;
                            return <li key={key}>{label}</li>;
                          })}
                        </ul>
                      </Trans>
                    ) : null
                  }
                  visible={
                    !isEveryRequiredFieldFilled ||
                    isAttachmentUploading ||
                    isCreatingAttachments
                  }
                  placement={"topRight"}
                  align={
                    !isEveryRequiredFieldFilled
                      ? tooltipAlign.fields
                      : tooltipAlign.attachment
                  }
                  overlayInnerStyle={styles.overlayInnerStyle}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion={"2.0"}
                    size={"small"}
                    variant={"progressive"}
                    type={"fill"}
                    icon={<SendFilled variant={"on"} />}
                    onClick={onSendMessageLocal}
                    disabled={
                      !isEveryRequiredFieldFilled ||
                      _isSendButtonDisabled ||
                      isAIGeneratingResponse ||
                      isAudioTranscriptLoading
                    }
                    testId={`assessment-aiContextBar-sendButton`}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* white linear gradient */}
      {showFooterOverlay ? <div className={footerOverlayClass} /> : null}

      {/* errors dialog box */}
      {!_.isEmpty(currentDialogAction) ? (
        <Modal
          width={"440px"}
          height={"fit-content"}
          shouldCloseOnEsc={true}
          isOpen={true}
          shouldCloseOnOverlayClick={false}
          onClose={onClickDialogButton}
        >
          <Modal.Header isClosable={true} hasSeparator={false}>
            <header className={classes.modalTitleContainer}>
              <WarningTriangleFilled variant={"critical"} />
              <span className={classes.modalTitle}>
                {DIALOGS_INFO[currentDialogAction].title({
                  t,
                  ...currentDialogParams,
                })}
              </span>
            </header>
          </Modal.Header>
          <Modal.Body isScrollable>
            <span className={classes.modalBody}>
              {DIALOGS_INFO[currentDialogAction].message({
                t,
                ...currentDialogParams,
              })}
            </span>
          </Modal.Body>
          <Modal.Footer hasSeparator={false}>
            <footer className={classes.modalFooter}>
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                size="medium"
                onClick={onClickDialogButton}
                testId={`assessment-aiContextBar-modalButtonTwo`}
              >
                {DIALOGS_INFO[currentDialogAction].buttonTwoText({
                  t,
                  ...currentDialogParams,
                })}
              </Button>
            </footer>
          </Modal.Footer>
        </Modal>
      ) : null}

      {showLinkifyModal ? (
        <LinkifyModal
          {...attachment}
          mode={selectedAttachmentId ? "edit" : "create"}
          title={_.get(attachment, "name")}
          onClose={onCloseLinkifyModal}
          onClickSave={onInsertAttachment}
          customFilterFilesHandler={customFilterFilesHandler}
          testId={`assessment-aiContextBar-linkifyModal`}
        />
      ) : null}

      {pendingTranscriptionUrls.length > 0 && (
        <TranscriptionListener onPusherEventTriggered={onTranscriptionEvent} />
      )}
    </footer>
  );
};

const mapActionCreators = {
  getContextFieldsWithValue,
  updateAIConversation,
  updateShowAIQuickTip,
  deleteAttachment,
  createAIConversationAttachments,
  deleteAIConversationAttachment,
  getSpeechToTextTranscript,
};

const mapStateToProps = (state, ownProps) => {
  const { aiConversationId } = ownProps;

  // data from redux store
  const curriculumType = getCurrentCurriculumProgramType(state);
  const userType = getUserType(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const showAIQuickTip = _.get(state, "assessment.showAIQuickTip", false);
  const progressOfUploads = _.get(state, "app_services.progressOfUploads");

  // planner data
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );

  const courseGradeFilters = {
    academicYearId,
  };

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const isAttachmentUploading = _.some(_.keys(progressOfUploads), key => {
    const { parentId, parentType } = progressOfUploads[key] || {};
    return parentId === aiConversationId && parentType === "AI_CONVERSATION";
  });

  return {
    academicYearId,
    collaboratorsFilter,
    curriculumType,
    userType,
    plannerElementSets,
    showAIQuickTip,
    courseGradeFilters,
    isAttachmentUploading,
  };
};

AIContextBar.propTypes = {
  value: PropTypes.string, // value of the content editable div
  onChange: PropTypes.func, // function to update the value of the content editable div
  selectedAction: PropTypes.object, // selected prompt from the library
  setSelectedAction: PropTypes.func, // function to update the selected prompt
  isInitialState: PropTypes.bool, // flag to indicate when chat is empty
  onSendMessage: PropTypes.func.isRequired, // function to send the message
  isSendButtonDisabled: PropTypes.bool, // flag to disable send button
  footerTopComp: PropTypes.node, // component to render on top of the footer
  context: PropTypes.object, // state to store local context fields (location, additional class info etc.) value.
  setContext: PropTypes.func, // function to update the local context fields
  excludedFields: PropTypes.array, // fields to exclude from the being asked as prerequisite or from context list
  setExcludedFields: PropTypes.func, // function to update the excluded fields
  setConfigureAIVoiceRef: PropTypes.func, // function to set the ref of ConfigureAIVoice component
  onClickFooterOption: PropTypes.func, // function to handle click event of footer options,
  entityId: PropTypes.string, // entity id like assessment id, unit plan id etc.
  entityType: PropTypes.string, // entity type like le, fmt, smt, worksheet etc.
  parentType: PropTypes.string, // parent type like ASSESSMENT, WORKSHEET etc.
  isWritingMessage: PropTypes.bool, // flag to indicate if AI is currently writing a message
  isQuestionAssistant: PropTypes.bool, // flag for worksheet assistant on question level
  isSpeechEnabled: PropTypes.bool, // flag to enabled speech to text
  question: PropTypes.object, // active question data
  chatInputPlaceholder: PropTypes.string, // custom placeholder from consumer
  isSuggestionLibraryDisabled: PropTypes.bool, // flag to disable suggestion library
  prompts: PropTypes.arrayOf(PropTypes.object), // all the prompts for the AI, used to exclude the fields which are not present in the planner template
  isAITutor: PropTypes.bool, // flag to indicate if AI Tutor assessment type
  templateId: PropTypes.string, // template id of the assessment
  isUnitPlanAI: PropTypes.bool, // flag to indicate if this is unit plan AI context bar
  onClickContextField: PropTypes.func, // function to handle click event of context field for unit plan fields
  isAttachmentUploadDisabled: PropTypes.bool, // flag to disable attachment upload
  showFooterOverlay: PropTypes.bool, // flag to show footer overlay
};

AIContextBar.defaultProps = {
  value: "",
  onChange: _.noop,
  selectedAction: {},
  setSelectedAction: _.noop,
  isInitialState: false,
  onSendMessage: _.noop,
  isSendButtonDisabled: false,
  footerTopComp: null,
  context: {},
  setContext: _.noop,
  excludedFields: [],
  setExcludedFields: _.noop,
  setConfigureAIVoiceRef: _.noop,
  onClickFooterOption: _.noop,
  isSuggestionLibraryDisabled: false,
  parentType: ASSESSMENT,
  isWritingMessage: false,
  isQuestionAssistant: false,
  isSpeechEnabled: false,
  question: {},
  chatInputPlaceholder: "",
  prompts: [],
  isAITutor: false,
  templateId: "",
  isUnitPlanAI: false,
  onClickContextField: _.noop,
  isAttachmentUploadDisabled: false,
  showFooterOverlay: true,
};

export default compose(
  I18nHOC({ resource: ["common", "ai", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators),
  getAssessmentDetailsForAIEnhancer,
  graphql(getUnitPlanCollaboratorsQuery, {
    name: "getUnitPlanCollaborators",
    skip: ({ unitPlanDetails }) => _.isEmpty(unitPlanDetails?.unitPlanId),
    options: ({ unitPlanDetails, collaboratorsFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: unitPlanDetails?.unitPlanId, collaboratorsFilter },
    }),
  })
)(AIContextBar);
